



import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle,
  PencilLine,
  Search,
  Sparkles,
  CheckCircle2,
  UsersRound,
  X,
} from "lucide-react";
import {
  getChatUsersApi,
  getChatGroupsApi,
  getEmployeesApi,
  getMyReportingApi,
  getReportingStructureApi,
} from "../../app/api";
 
import ChatWindow from "./ChatWindow";
import CreateGroupModal from "./CreateGroupModal";
import GroupMembersModal from "./GroupMembersModal";
import { getSocket } from "../../socket/socket";
import { useCallStore } from "../../app/callStore";
import { getGroupImageUrl, getMediaUrl, getStoredUser, safeJsonParse } from "./chatUtils";

function readInitialChatTarget(initialChatTarget) {
  if (initialChatTarget) return initialChatTarget;
  if (window.__OPEN_CHAT_TARGET__) return window.__OPEN_CHAT_TARGET__;

  return safeJsonParse(localStorage.getItem("openChatFromNotification"));
}

function buildPendingSelection(target) {
  const groupId = target?.groupId || target?.group_id;
  const userId = target?.userId || target?.user_id || window.__OPEN_CHAT_USER_ID__;

  if (groupId) {
    return {
      selectedGroup: {
        id: groupId,
        name: target?.groupName || target?.group_name || "Group",
        isGroup: true,
      },
      selectedUser: null,
    };
  }

  if (userId) {
    return {
      selectedUser: {
        id: userId,
        first_name: target?.firstName || target?.first_name || target?.senderName || "Chat",
        last_name: target?.lastName || target?.last_name || "",
        profile_pic: target?.profilePic || target?.profile_pic || target?.senderProfilePic || target?.sender_profile_pic || "",
      },
      selectedGroup: null,
    };
  }

  return { selectedUser: null, selectedGroup: null };
}

const normalizeOnlineUserId = (value) => {
  if (value && typeof value === "object") {
    return String(value.userId || value.user_id || value.id || value.user?.id || "");
  }

  return String(value || "");
};

const isTruthyPresence = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

const RECENT_CHAT_ACTIVITY_KEY = "uptaskRecentChatActivity";
const RECENT_CHAT_ACTIVITY_MAX_AGE_MS = 2 * 60 * 1000;

const splitDisplayName = (name = "User") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  return {
    first_name: parts[0] || "User",
    last_name: parts.slice(1).join(" "),
  };
};

const getReportingUserIds = (groups = []) =>
  new Set(
    groups.flatMap((group) => [
      ...(group.directorIds || []),
      group.managerId,
      ...(group.employeeIds || []),
      ...(group.directors || []).map((person) => person.id),
      group.manager?.id,
      group.reportingManager?.id,
      ...(group.employees || []).map((person) => person.id),
      ...(group.teamMembers || []).map((person) => person.id),
    ]).filter(Boolean).map(String)
  );
 
export default function ChatDrawer({ employees, onClose, initialChatTarget }) {
  const [pendingTarget, setPendingTarget] = useState(() => readInitialChatTarget(initialChatTarget));
  const pendingSelection = buildPendingSelection(pendingTarget);
  const [selectedUser, setSelectedUser] = useState(pendingSelection.selectedUser);
  const [selectedGroup, setSelectedGroup] = useState(pendingSelection.selectedGroup);
  const [groups, setGroups] = useState([]);
  const [chatUsersList, setChatUsersList] = useState([]);
  const { activeGroupCalls } = useCallStore();
  const [allEmployees, setAllEmployees] = useState(employees || []);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [inspectGroup, setInspectGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [chatFilter, setChatFilter] = useState("all");
  const [reportingUserIds, setReportingUserIds] = useState(null);
 
 
 
 
  const [onlineUsers, setOnlineUsers] = useState([]);
 
 
 
 
  const searchRef = useRef(null);
 
  const currentUser = getStoredUser();
  const isAdmin = [1, 2, 3].includes(currentUser.role_id);

  const isCurrentTeamUser = (item) => {
    if (!item?.id || String(item.id) === String(currentUser?.id)) return true;
    if (!reportingUserIds) return true;
    if (reportingUserIds.size === 0) return true;
    return reportingUserIds.has(String(item.id));
  };

  const upsertDirectConversation = (personId, data = {}, options = {}) => {
    if (!personId) return;

    const messageTime = data.createdAt || data.created_at || data.last_message_time || new Date().toISOString();
    const displayName =
      options.name ||
      data.senderName ||
      data.sender_name ||
      data.receiverName ||
      data.receiver_name ||
      `${data.first_name || ""} ${data.last_name || ""}`.trim() ||
      "User";
    const nameParts = splitDisplayName(displayName);
    const profilePic =
      options.profilePic ||
      data.senderProfilePic ||
      data.sender_profile_pic ||
      data.receiverProfilePic ||
      data.receiver_profile_pic ||
      data.profile_pic ||
      "";

    setChatUsersList((prev) => {
      const existing = prev.find((chatUser) => String(chatUser.id) === String(personId));
      const unreadIncrement = options.incrementUnread && String(selectedUser?.id) !== String(personId) ? 1 : 0;
      const updated = {
        ...(existing || {}),
        id: personId,
        first_name: existing?.first_name || nameParts.first_name,
        last_name: existing?.last_name || nameParts.last_name,
        profile_pic: existing?.profile_pic || profilePic,
        last_message_time: messageTime,
        created_at: existing?.created_at || messageTime,
        unread_count: Number(existing?.unread_count || 0) + unreadIncrement,
      };

      return [
        updated,
        ...prev.filter((chatUser) => String(chatUser.id) !== String(personId)),
      ];
    });
  };

  const upsertGroupConversation = (groupId, data = {}, options = {}) => {
    if (!groupId) return;

    const messageTime = data.createdAt || data.created_at || data.last_message_time || new Date().toISOString();
    setGroups((prev) => {
      const existing = prev.find((group) => String(group.id) === String(groupId));
      const unreadIncrement = options.incrementUnread && String(selectedGroup?.id) !== String(groupId) ? 1 : 0;
      const updated = {
        ...(existing || {}),
        id: groupId,
        name: existing?.name || data.groupName || data.group_name || "Group",
        isGroup: true,
        last_message_time: messageTime,
        created_at: existing?.created_at || messageTime,
        unread_count: Number(existing?.unread_count || 0) + unreadIncrement,
      };

      return [
        updated,
        ...prev.filter((group) => String(group.id) !== String(groupId)),
      ];
    });
  };

  const applyChatListActivity = (data = {}, options = {}) => {
    const groupId = data.groupId || data.group_id;
    if (groupId) {
      upsertGroupConversation(groupId, data, options);
      return;
    }

    const personId = options.incoming
      ? data.senderId || data.sender_id
      : data.receiverId || data.receiver_id || data.senderId || data.sender_id;

    upsertDirectConversation(personId, data, {
      ...options,
      name:
        options.name ||
        data.receiverName ||
        data.receiver_name ||
        data.senderName ||
        data.sender_name,
      profilePic:
        options.profilePic ||
        data.receiverProfilePic ||
        data.receiver_profile_pic ||
        data.senderProfilePic ||
      data.sender_profile_pic,
    });
  };

  const readFreshRecentChatActivity = () => {
    const rawActivity = localStorage.getItem(RECENT_CHAT_ACTIVITY_KEY);
    if (!rawActivity) return null;

    try {
      const activity = JSON.parse(rawActivity);
      const createdAt = new Date(activity.createdAt || activity.created_at || 0).getTime();
      if (!createdAt || Date.now() - createdAt > RECENT_CHAT_ACTIVITY_MAX_AGE_MS) {
        localStorage.removeItem(RECENT_CHAT_ACTIVITY_KEY);
        return null;
      }

      return activity;
    } catch {
      localStorage.removeItem(RECENT_CHAT_ACTIVITY_KEY);
      return null;
    }
  };
 
  const onlineBlinkStyles = `
    @keyframes uptaskOnlineBlink {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
        box-shadow: 0 0 0 4px rgba(255,255,255,0.16), 0 0 18px rgba(255,255,255,0.95), 0 0 24px rgba(239,68,68,0.9);
      }
      50% {
        opacity: 0.45;
        transform: scale(0.72);
        box-shadow: 0 0 0 7px rgba(255,255,255,0.08), 0 0 28px rgba(255,255,255,0.78), 0 0 34px rgba(239,68,68,0.55);
      }
    }
  `;

  // ================= SOCKET (UNREAD COUNT) =================
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
 
 
    const applyOnlineUsers = (users = []) => {
      setOnlineUsers(
        users
          .map(normalizeOnlineUserId)
          .filter(Boolean)
      );
    };

    const emitTabPresence = () => {
      const isActive = document.visibilityState === "visible";
      socket.emit("uptask_tab_active", { active: isActive });
      socket.emit("chat_tab_active", { active: isActive });
    };

    emitTabPresence();
    document.addEventListener("visibilitychange", emitTabPresence);

// 🔥 get initial online users
socket.on("online_users", applyOnlineUsers);
 
// 🟢 user came online
socket.on("user_online", (payload = {}) => {
  // setOnlineUsers((prev) => [...new Set([...prev, userId])]);
  const userId = normalizeOnlineUserId(payload.userId || payload.user_id || payload.id || payload);
  if (!userId) return;
  setOnlineUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
});
 
// 🔴 user went offline
socket.on("user_offline", (payload = {}) => {
  const userId = normalizeOnlineUserId(payload.userId || payload.user_id || payload.id || payload);
  if (!userId) return;
  setOnlineUsers((prev) => prev.filter((id) => id !== userId));
});
 
 
 
 
 
    const handleRecv = (data) => {
      applyChatListActivity(data, { incoming: true, incrementUnread: true });
    };

    const handleSentForList = (data) => {
      applyChatListActivity(data);
    };

    const handleLocalSentForList = (event) => {
      handleSentForList(event.detail || {});
    };
 
    socket.on("receive_message", handleRecv);
    socket.on("message_sent", handleSentForList);
    window.addEventListener("uptask:chat-list-message-sent", handleLocalSentForList);
 
    // return () => {
    //   socket.off("receive_message", handleRecv);
    // };
 
      // ================= CLEANUP =================
  return () => {
    document.removeEventListener("visibilitychange", emitTabPresence);
    socket.emit("uptask_tab_active", { active: false });
    socket.emit("chat_tab_active", { active: false });
    socket.off("online_users");
    socket.off("user_online");
    socket.off("user_offline");
    socket.off("receive_message", handleRecv);
    socket.off("message_sent", handleSentForList);
    window.removeEventListener("uptask:chat-list-message-sent", handleLocalSentForList);
  };
  }, [selectedUser?.id, selectedGroup?.id]);

  useEffect(() => {
    const activity = readFreshRecentChatActivity();
    if (activity) applyChatListActivity(activity);
  }, []);
 
  // ================= FETCH GROUPS =================
  const fetchGroups = async () => {
    try {
      const res = await getChatGroupsApi();
      if (res?.success) {
        setGroups(res.groups || []);
      }
    } catch (err) {
      console.error("Failed to fetch groups", err);
    }
  };
 
  // ================= FETCH USERS =================
  const fetchChatUsers = async () => {
    try {
      const res = await getChatUsersApi();
      if (res?.success) {
        setChatUsersList((res.users || res.data || []).filter(isCurrentTeamUser));
        const recentActivity = readFreshRecentChatActivity();
        if (recentActivity) {
          window.setTimeout(() => {
            applyChatListActivity(recentActivity);
            localStorage.removeItem(RECENT_CHAT_ACTIVITY_KEY);
          }, 0);
        }
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };
 
  const fetchSearchResults = async (query) => {
    try {
      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      if (!reportingUserIds) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      const res = await getChatUsersApi(query);
      if (res?.success) {
        setSearchResults((res.users || res.data || []).filter(isCurrentTeamUser));
      }
    } catch (err) {
      console.error("Failed to search users", err);
    }
  };

  const fetchReportingUsers = async () => {
    try {
      const res = isAdmin ? await getReportingStructureApi() : await getMyReportingApi();
      if (res?.success) {
        setReportingUserIds(getReportingUserIds(res.data?.groups || []));
      } else {
        setReportingUserIds(new Set());
      }
    } catch (err) {
      console.error("Failed to fetch reporting users", err);
      setReportingUserIds(new Set());
    }
  };
 
  // Load groups and employees on mount (we only want users with chat history)
  useEffect(() => {
    fetchReportingUsers();
    fetchGroups();
   
    // Fallback: If employees weren't passed, fetch all for the Create Group modal
    if (!employees || employees.length === 0) {
      getEmployeesApi().then(res => {
        if (res?.success) {
          setAllEmployees(res.data || res.users || []);
        }
      });
    } else {
      setAllEmployees(employees);
    }
  }, [employees]);

  useEffect(() => {
    if (!reportingUserIds) return;
    fetchChatUsers();
  }, [reportingUserIds]);
 
  // Search debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchSearchResults(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery, reportingUserIds]);
 
  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearching(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  // Reset unread count locally on open
  useEffect(() => {
    if (selectedUser) {
      setChatUsersList(prev => prev.map(u =>
        u.id === selectedUser.id ? { ...u, unread_count: 0 } : u
      ));
    }
  }, [selectedUser]);
 
  useEffect(() => {
    if (selectedGroup) {
      setGroups(prev => prev.map(g =>
        g.id === selectedGroup.id ? { ...g, unread_count: 0 } : g
      ));
    }
  }, [selectedGroup]);
 
 
  // ================= COMBINE & SORT =================
  const combinedChats = [
    ...groups.map(g => ({ ...g, isGroup: true })),
    ...chatUsersList.map(u => ({ ...u, isGroup: false }))
  ].sort((a, b) => {
    const timeA = new Date(a.last_message_time || a.created_at || 0).getTime();
    const timeB = new Date(b.last_message_time || b.created_at || 0).getTime();
    return timeB - timeA;
  });
 
 
  const directChatsCount = combinedChats.filter((chat) => !chat.isGroup).length;
  const groupChatsCount = combinedChats.filter((chat) => chat.isGroup).length;
  const unreadChatsCount = combinedChats.reduce(
    (total, chat) => total + Number(chat.unread_count || 0),
    0
  );
  const filteredChats = combinedChats.filter((chat) => {
    if (chatFilter === "direct") return !chat.isGroup;
    if (chatFilter === "groups") return chat.isGroup;
    if (chatFilter === "unread") return Number(chat.unread_count || 0) > 0;
    return true;
  });

  const setConversationFilter = (filter) => {
    setChatFilter((current) => (current === filter ? "all" : filter));
  };

  const clearUnreadMessages = () => {
    const socket = getSocket();

    chatUsersList.forEach((user) => {
      if (Number(user.unread_count || 0) > 0) {
        socket?.emit("mark_seen", { senderId: user.id });
      }
    });

    groups.forEach((group) => {
      if (Number(group.unread_count || 0) > 0) {
        socket?.emit("mark_group_seen", { groupId: group.id });
      }
    });

    setChatUsersList((prev) => prev.map((user) => ({ ...user, unread_count: 0 })));
    setGroups((prev) => prev.map((group) => ({ ...group, unread_count: 0 })));
    if (chatFilter === "unread") setChatFilter("all");
  };
 
  // useEffect(() => {
  //   const userId = window.__OPEN_CHAT_USER_ID__;
 
  //   if (userId && chatUsersList.length > 0) {
  //     const user = chatUsersList.find(u => u.id === userId);
 
  //     if (user) {
  //       setSelectedUser(user);
 
  //       // clear after use
  //       window.__OPEN_CHAT_USER_ID__ = null;
  //     }
  //   }
  // }, [chatUsersList]);
 


  useEffect(() => {
    const data = pendingTarget || readInitialChatTarget(initialChatTarget);

    const groupId = data?.groupId || data?.group_id;
    const userId = data?.userId || data?.user_id || window.__OPEN_CHAT_USER_ID__;

    if (groupId) {
      const group = groups.find((g) => String(g.id) === String(groupId));
      if (group || groups.length > 0) {
        setSelectedGroup(group || { id: groupId, name: data?.groupName || data?.group_name || "Group" });
        setSelectedUser(null);
        localStorage.removeItem("openChatFromNotification");
        window.__OPEN_CHAT_TARGET__ = null;
        window.__OPEN_CHAT_USER_ID__ = null;
        setPendingTarget(null);
      }
      return;
    }

    if (userId && chatUsersList.length > 0) {
      const user = chatUsersList.find((u) => String(u.id) === String(userId));

      if (user) {
        setSelectedUser(user);
        setSelectedGroup(null);
        localStorage.removeItem("openChatFromNotification");
        window.__OPEN_CHAT_TARGET__ = null;
        window.__OPEN_CHAT_USER_ID__ = null;
        setPendingTarget(null);
      }
    }
  }, [initialChatTarget, pendingTarget, chatUsersList, groups]);




  const formatChatTime = (date) => {
    if (!date) return "";
  
    const d = new Date(date);
    const now = new Date();
  
    const isToday = d.toDateString() === now.toDateString();
  
    if (isToday) {
      // show time
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else {
      // show date
      return d.toLocaleDateString([], { day: "2-digit", month: "short" });
    }
  };

  const handleGroupUpdated = (updatedGroup) => {
    setGroups((prev) =>
      prev.map((group) =>
        String(group.id) === String(updatedGroup.id)
          ? { ...group, ...updatedGroup, isGroup: true }
          : group
      )
    );
    setSelectedGroup((prev) =>
      prev && String(prev.id) === String(updatedGroup.id)
        ? { ...prev, ...updatedGroup, isGroup: true }
        : prev
    );
    setInspectGroup((prev) =>
      prev && String(prev.id) === String(updatedGroup.id)
        ? { ...prev, ...updatedGroup, isGroup: true }
        : prev
    );
    fetchGroups();
  };

  const getChatName = (item) =>
    item.isGroup ? item.name : `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unknown";

  const isUserOnline = (item) => {
    if (!item || item.isGroup) return false;

    return (
      onlineUsers.includes(String(item.id)) ||
      isTruthyPresence(item.is_online) ||
      isTruthyPresence(item.isOnline) ||
      isTruthyPresence(item.online) ||
      isTruthyPresence(item.active_on_uptask) ||
      isTruthyPresence(item.activeOnUptask) ||
      isTruthyPresence(item.is_tab_active) ||
      isTruthyPresence(item.isTabActive)
    );
  };

  const getChatSubtitle = (item) => {
    if (item.isGroup) {
      return activeGroupCalls.some((call) => call.groupId === item.id)
        ? "Active group call"
        : "Team group";
    }

    return isUserOnline(item) ? "Online now" : "Direct message";
  };

  const getUnreadCount = (item) => Number(item.unread_count || 0);

  const renderChatAvatar = (item) => {
    if (item.isGroup) {
      const groupImageUrl = getGroupImageUrl(item);
      return (
        <div className="relative h-[52px] w-[52px] shrink-0">
          <div className="flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-2xl border border-cyan-200/20 bg-gradient-to-br from-cyan-500 via-violet-500 to-fuchsia-600 font-bold text-white shadow-[0_14px_28px_rgba(91,33,182,0.32)]">
            {groupImageUrl ? (
              <img src={groupImageUrl} alt={item.name || "Group"} className="h-full w-full object-cover" />
            ) : (
              <UsersRound size={22} />
            )}
          </div>
          {activeGroupCalls.some((call) => call.groupId === item.id) && (
            <span className="absolute -right-1 -top-1 h-3.5 w-3.5 animate-pulse rounded-full border-2 border-[#15111f] bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.9)]" />
          )}
        </div>
      );
    }

    return (
      <div className="relative h-[52px] w-[52px] shrink-0">
        <div className="h-[52px] w-[52px] overflow-hidden rounded-2xl bg-white/15 ring-1 ring-white/12 shadow-[0_12px_26px_rgba(0,0,0,0.25)]">
          {item.profile_pic ? (
            <img
              src={getMediaUrl(item.profile_pic)}
              alt={`${item.first_name || "User"} profile`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-600 to-fuchsia-600 text-lg font-black text-white">
              {item.first_name?.charAt(0) || "U"}
            </div>
          )}
        </div>
        {isUserOnline(item) && (
          <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-red-500 shadow-[0_0_0_4px_rgba(255,255,255,0.18),0_0_18px_rgba(255,255,255,0.95),0_0_24px_rgba(239,68,68,0.9)] animate-[uptaskOnlineBlink_1.05s_ease-in-out_infinite]" />
        )}
      </div>
    );
  };


  return createPortal(
    <div className="fixed inset-0 z-[9999] flex justify-end sm:p-5">
      <style>{onlineBlinkStyles}</style>
 
      {/* BACKDROP */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-md"
      />
 
      {/* DRAWER */}
      <div
        className="relative h-full w-full overflow-hidden bg-[#12101a] text-white shadow-[0_24px_80px_rgba(0,0,0,0.45)] sm:w-[430px] sm:rounded-[30px] sm:border sm:border-white/15"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_8%,rgba(34,211,238,0.16),transparent_28%),radial-gradient(circle_at_86%_2%,rgba(168,85,247,0.24),transparent_30%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_42%)]" />
        <div className="relative flex h-full flex-col">
 
        {/* HEADER */}
        <div className="border-b border-white/10 bg-white/[0.04] px-5 pb-5 pt-5 backdrop-blur-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative flex h-12 w-12 items-center justify-center rounded-[20px] bg-gradient-to-br from-cyan-400 via-violet-500 to-fuchsia-600 text-white shadow-[0_18px_36px_rgba(124,58,237,0.36)] ring-1 ring-white/20">
                <MessageCircle size={23} strokeWidth={2.5} />
                <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border-2 border-[#211832] bg-emerald-400 text-[#062a1e]">
                  <Sparkles size={11} strokeWidth={2.6} />
                </span>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200/70">Uptask</p>
                <h3 className="mt-0.5 text-lg font-black tracking-tight">
                  Team Chat
                </h3>
                <p className="mt-0.5 text-xs text-white/48">
                  {combinedChats.length} conversations ready
                </p>
              </div>
            </div>
 
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setShowCreateGroup(true)}
                title="Create Group"
                className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/12 bg-white/[0.09] text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-white/[0.16]"
              >
                <PencilLine size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose?.();
              }}
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/12 bg-white/[0.09] text-white/80 transition hover:-translate-y-0.5 hover:bg-white/[0.16] hover:text-white"
              title="Close chat"
              aria-label="Close chat"
            >
              <X size={19} />
            </button>
          </div>
          </div>

          {!selectedUser && !selectedGroup && (
            <div className="mt-5 grid grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => setConversationFilter("direct")}
                className={`rounded-2xl border px-3 py-3 text-left shadow-inner shadow-white/[0.02] transition hover:-translate-y-0.5 hover:border-cyan-100/35 ${
                  chatFilter === "direct"
                    ? "border-cyan-100/45 bg-cyan-300/[0.18] ring-2 ring-cyan-300/20"
                    : "border-cyan-200/15 bg-cyan-300/[0.08]"
                }`}
              >
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-cyan-100/55">Direct</p>
                <p className="mt-1 text-lg font-black leading-none">{directChatsCount}</p>
              </button>
              <button
                type="button"
                onClick={() => setConversationFilter("groups")}
                className={`rounded-2xl border px-3 py-3 text-left shadow-inner shadow-white/[0.02] transition hover:-translate-y-0.5 hover:border-fuchsia-100/35 ${
                  chatFilter === "groups"
                    ? "border-fuchsia-100/45 bg-fuchsia-300/[0.18] ring-2 ring-fuchsia-300/20"
                    : "border-fuchsia-200/15 bg-fuchsia-300/[0.08]"
                }`}
              >
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-fuchsia-100/55">Groups</p>
                <p className="mt-1 text-lg font-black leading-none">{groupChatsCount}</p>
              </button>
              <button
                type="button"
                onClick={() => setConversationFilter("unread")}
                className={`rounded-2xl border px-3 py-3 text-left shadow-inner shadow-white/[0.02] transition hover:-translate-y-0.5 hover:border-rose-100/35 ${
                  chatFilter === "unread"
                    ? "border-rose-100/45 bg-rose-300/[0.18] ring-2 ring-rose-300/20"
                    : "border-rose-200/15 bg-rose-300/[0.08]"
                }`}
              >
                <p className="text-[9px] font-black uppercase tracking-[0.18em] text-rose-100/55">Unread</p>
                <p className="mt-1 text-lg font-black leading-none text-rose-50">{unreadChatsCount}</p>
              </button>
            </div>
          )}
          {!selectedUser && !selectedGroup && unreadChatsCount > 0 && (
            <button
              type="button"
              onClick={clearUnreadMessages}
              className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-white/12 bg-white/[0.08] px-3 py-2 text-xs font-black text-white/70 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.14] hover:text-white"
            >
              <CheckCircle2 size={15} />
              Clear unread messages
            </button>
          )}
        </div>
 
        {/* SEARCH BAR */}
        {!selectedUser && !selectedGroup && (
          <div className="relative z-50 border-b border-white/10 bg-black/[0.12] px-5 py-4">
            <div className="relative" ref={searchRef}>
              <input
                type="text"
                placeholder="Search users to start a chat..."
                value={searchQuery}
                onFocus={() => setIsSearching(searchQuery.trim().length > 0)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearching(e.target.value.trim().length > 0);
                }}
                className="w-full rounded-[20px] border border-white/12 bg-white/[0.075] py-3.5 pl-12 pr-11 text-sm text-white outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_14px_30px_rgba(0,0,0,0.18)] transition placeholder:text-white/34 focus:border-cyan-200/45 focus:bg-white/[0.105] focus:ring-4 focus:ring-cyan-400/10"
              />
              <Search className="pointer-events-none absolute left-4 top-4 h-4 w-4 text-cyan-100/45" />
 
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setIsSearching(false);
                    fetchSearchResults("");
                  }}
                  className="absolute right-3 top-3.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white/50 transition hover:bg-white/20 hover:text-white"
                >
                  <X size={13} />
                </button>
              )}
 
              {/* DROPDOWN */}
              {isSearching && (
                <div className="custom-scrollbar absolute left-0 top-full z-[10000] mt-2 max-h-72 w-full overflow-y-auto rounded-3xl border border-white/12 bg-[#201b2b]/95 p-1.5 shadow-2xl shadow-black/45 backdrop-blur-xl">
                  {searchResults.length === 0 ? (
                    <div className="p-5 text-center text-sm text-white/45">No users found</div>
                  ) : (
                    searchResults.map(u => (
                      <div
                        key={u.id}
                        onClick={() => {
                          setSelectedUser(u);
                          setIsSearching(false);
                          setSearchQuery("");
                        }}
                        className="flex cursor-pointer items-center gap-3 rounded-2xl p-3 transition hover:bg-white/10"
                      >
                        <div className="h-9 w-9 overflow-hidden rounded-full bg-white/20 ring-1 ring-white/10">
                          {u.profile_pic ? (
                            <img src={getMediaUrl(u.profile_pic)} alt={`${u.first_name || "User"} profile`} className="h-full w-full object-cover"/>
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-violet-600 text-xs font-semibold text-white">
                              {u.first_name?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                          {/* <p className="text-[10px] text-gray-400">{u.email}</p> */}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
 
        {/* BODY */}
        <div
          className={`custom-scrollbar flex flex-1 flex-col overflow-y-auto ${
            selectedUser || selectedGroup ? "p-0" : "px-4 py-4"
          }`}
        >
 
          {selectedUser || selectedGroup ? (
            <ChatWindow
              user={selectedUser}
              group={selectedGroup}
              currentUser={currentUser}
              onBack={() => {
                setSelectedUser(null);
                setSelectedGroup(null);
                // Refresh list on back to get updated times/unread
                fetchGroups();
                fetchChatUsers();
              }}
              onInspectGroup={() => setInspectGroup(selectedGroup)}
              onGroupDeleted={() => {
                setSelectedGroup(null);
                fetchGroups();
              }}
            />
          ) : (
            <div className="flex flex-col gap-2.5 pb-10">
              {filteredChats.length === 0 && (
                <div className="rounded-[24px] border border-white/10 bg-white/[0.045] px-4 py-8 text-center">
                  <p className="text-sm font-bold text-white/70">No conversations found</p>
                  <p className="mt-1 text-xs text-white/40">
                    {chatFilter === "unread"
                      ? "Unread chats will appear here."
                      : "Tap the active filter again to show all chats."}
                  </p>
                </div>
              )}

              {filteredChats.map(item => {
                const unread = getUnreadCount(item);
                return (
                  <button
                    key={`${item.isGroup ? "g" : "u"}_${item.id}`}
                    onClick={() => (item.isGroup ? setSelectedGroup(item) : setSelectedUser(item))}
                    className={`group relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-[24px] border p-3 text-left transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(0,0,0,0.28)] ${
                      unread
                        ? "border-cyan-200/18 bg-gradient-to-r from-cyan-400/[0.11] via-white/[0.07] to-violet-400/[0.10]"
                        : "border-white/8 bg-white/[0.045] hover:border-white/14 hover:bg-white/[0.075]"
                    }`}
                  >
                    <span className="pointer-events-none absolute inset-y-3 left-0 w-1 rounded-r-full bg-gradient-to-b from-cyan-300 to-violet-400 opacity-0 transition group-hover:opacity-100" />
                    {renderChatAvatar(item)}

                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="truncate text-[15px] font-black tracking-tight text-white">
                          {getChatName(item)}
                        </p>
                        {item.isGroup && (
                          <span className="rounded-full border border-white/10 bg-white/[0.07] px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-white/45">
                            Group
                          </span>
                        )}
                      </div>
                      <p className={`mt-1 truncate text-xs ${item.isGroup ? "text-cyan-100/50" : isUserOnline(item) ? "font-semibold text-red-100/85" : "text-white/42"}`}>
                        {getChatSubtitle(item)}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className="text-[11px] font-medium text-white/35">
                        {formatChatTime(item.last_message_time || item.created_at)}
                      </span>
                      {unread > 0 ? (
                        <span className="grid h-6 min-w-6 place-items-center rounded-full bg-gradient-to-br from-rose-400 to-red-600 px-1.5 text-[10px] font-black text-white shadow-[0_8px_18px_rgba(244,63,94,0.32)]">
                          {unread > 99 ? "99+" : unread}
                        </span>
                      ) : isUserOnline(item) ? (
                        <span className="h-3 w-3 rounded-full border-2 border-white bg-red-500 shadow-[0_0_0_4px_rgba(255,255,255,0.12),0_0_18px_rgba(255,255,255,0.85),0_0_22px_rgba(239,68,68,0.9)] animate-[uptaskOnlineBlink_1.05s_ease-in-out_infinite]" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-white/10 transition group-hover:bg-cyan-200/50" />
                      )}
                    </div>
                  </button>
                );
              })}

              {combinedChats.length === 0 && (
                 <div className="mx-3 mt-10 rounded-3xl border border-dashed border-white/15 bg-white/[0.03] px-5 py-8 text-center">
                   <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-100">
                     <MessageCircle size={22} />
                   </div>
                   <p className="text-sm font-semibold text-white">No chats yet</p>
                   <p className="mt-1 text-xs leading-5 text-white/45">
                     Search for a teammate above to start a conversation.
                   </p>
                 </div>
              )}
 
            </div>
          )}
        </div>
 
        {/* MODALS */}
        {showCreateGroup && (
          <CreateGroupModal
            employees={allEmployees}
            onClose={() => setShowCreateGroup(false)}
            onGroupCreated={fetchGroups}
          />
        )}
 
        {inspectGroup && (
          <GroupMembersModal
            group={inspectGroup}
            onClose={() => setInspectGroup(null)}
            employees={allEmployees}
            isAdmin={isAdmin}
            onGroupUpdated={handleGroupUpdated}
          />
        )}
        </div>
      </div>
    </div>,
    document.body
  );
}




// import { useState, useEffect, useRef } from "react";
// import { createPortal } from "react-dom";
// import {
//   getChatUsersApi,
//   getChatGroupsApi,
//   getEmployeesApi
// } from "../../app/api";

// import ChatWindow from "./ChatWindow";
// import CreateGroupModal from "./CreateGroupModal";
// import GroupMembersModal from "./GroupMembersModal";
// import { getSocket } from "../../socket/socket";

// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

// export default function ChatDrawer({ employees, onClose }) {
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [selectedGroup, setSelectedGroup] = useState(null);
//   const [groups, setGroups] = useState([]);
//   const [chatUsersList, setChatUsersList] = useState([]);
//   const [allEmployees, setAllEmployees] = useState(employees || []);
//   const [showCreateGroup, setShowCreateGroup] = useState(false);
//   const [inspectGroup, setInspectGroup] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [searchResults, setSearchResults] = useState([]);
//   const [isSearching, setIsSearching] = useState(false);




//   const [onlineUsers, setOnlineUsers] = useState([]);



  
//   const searchRef = useRef(null);

//   const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
//   const isAdmin = [1, 2, 3].includes(currentUser.role_id);

//   // ================= SOCKET (UNREAD COUNT) =================
//   useEffect(() => {
//     const socket = getSocket();
//     if (!socket) return;


//     // 🔥 get initial online users
// socket.on("online_users", (users) => {
//   setOnlineUsers(users);
// });

// // 🟢 user came online
// socket.on("user_online", ({ userId }) => {
//   // setOnlineUsers((prev) => [...new Set([...prev, userId])]);
//   setOnlineUsers((prev) =>
//   prev.includes(userId) ? prev : [...prev, userId]
// );
// });

// // 🔴 user went offline
// socket.on("user_offline", ({ userId }) => {
//   setOnlineUsers((prev) => prev.filter((id) => id !== userId));
// });





//     const handleRecv = (data) => {
//       // Update user unread
//       setChatUsersList((prev) =>
//         prev.map((u) =>
//           u.id === data.senderId && selectedUser?.id !== data.senderId
//             ? {
//                 ...u,
//                 unread_count: (u.unread_count || 0) + 1,
//                 last_message_time: data.createdAt,
//               }
//             : u
//         )
//       );

//       // Update group unread
//       if (data.groupId) {
//         setGroups((prev) =>
//           prev.map((g) =>
//             g.id === data.groupId && selectedGroup?.id !== data.groupId
//               ? { ...g, unread_count: (g.unread_count || 0) + 1, last_message_time: data.createdAt }
//               : g
//           )
//         );
//       }
//     };

//     socket.on("receive_message", handleRecv);

//     // return () => {
//     //   socket.off("receive_message", handleRecv);
//     // };

//       // ================= CLEANUP =================
//   return () => {
//     socket.off("online_users");
//     socket.off("user_online");
//     socket.off("user_offline");
//     socket.off("receive_message", handleRecv);
//   };
//   }, [selectedUser?.id, selectedGroup?.id]);

//   // ================= FETCH GROUPS =================
//   const fetchGroups = async () => {
//     try {
//       const res = await getChatGroupsApi();
//       if (res?.success) {
//         setGroups(res.groups || []);
//       }
//     } catch (err) {
//       console.error("Failed to fetch groups", err);
//     }
//   };

//   // ================= FETCH USERS =================
//   const fetchChatUsers = async () => {
//     try {
//       const res = await getChatUsersApi();
//       if (res?.success) {
//         setChatUsersList(res.users || res.data || []);
//       }
//     } catch (err) {
//       console.error("Failed to fetch users", err);
//     }
//   };

//   const fetchSearchResults = async (query) => {
//     try {
//       if (!query.trim()) {
//         setSearchResults([]);
//         setIsSearching(false);
//         return;
//       }
//       setIsSearching(true);
//       const res = await getChatUsersApi(query);
//       if (res?.success) {
//         setSearchResults(res.users || res.data || []);
//       }
//     } catch (err) {
//       console.error("Failed to search users", err);
//     }
//   };

//   // Load groups and employees on mount (we only want users with chat history)
//   useEffect(() => {
//     fetchGroups();
//     fetchChatUsers();
    
//     // Fallback: If employees weren't passed, fetch all for the Create Group modal
//     if (!employees || employees.length === 0) {
//       getEmployeesApi().then(res => {
//         if (res?.success) {
//           setAllEmployees(res.data || res.users || []);
//         }
//       });
//     } else {
//       setAllEmployees(employees);
//     }
//   }, [employees]);

//   // Search debounce
//   useEffect(() => {
//     const handler = setTimeout(() => {
//       fetchSearchResults(searchQuery);
//     }, 300);
//     return () => clearTimeout(handler);
//   }, [searchQuery]);

//   // Close dropdown when clicked outside
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (searchRef.current && !searchRef.current.contains(e.target)) {
//         setIsSearching(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // Reset unread count locally on open
//   useEffect(() => {
//     if (selectedUser) {
//       setChatUsersList(prev => prev.map(u => 
//         u.id === selectedUser.id ? { ...u, unread_count: 0 } : u
//       ));
//     }
//   }, [selectedUser]);

//   useEffect(() => {
//     if (selectedGroup) {
//       setGroups(prev => prev.map(g => 
//         g.id === selectedGroup.id ? { ...g, unread_count: 0 } : g
//       ));
//     }
//   }, [selectedGroup]);


//   // ================= COMBINE & SORT =================
//   const combinedChats = [
//     ...groups.map(g => ({ ...g, isGroup: true })),
//     ...chatUsersList.map(u => ({ ...u, isGroup: false }))
//   ].sort((a, b) => {
//     const timeA = new Date(a.last_message_time || a.created_at || 0).getTime();
//     const timeB = new Date(b.last_message_time || b.created_at || 0).getTime();
//     return timeB - timeA;
//   });



//   useEffect(() => {
//     const userId = window.__OPEN_CHAT_USER_ID__;
  
//     if (userId && chatUsersList.length > 0) {
//       const user = chatUsersList.find(u => u.id === userId);
  
//       if (user) {
//         setSelectedUser(user);
  
//         // clear after use
//         window.__OPEN_CHAT_USER_ID__ = null;
//       }
//     }
//   }, [chatUsersList]);

//   return createPortal(
//     <div className="fixed inset-0 z-[9999] flex justify-end">

//       {/* BACKDROP */}
//       <div
//         onClick={onClose}
//         className="absolute inset-0 bg-black/50 backdrop-blur-md"
//       />

//       {/* DRAWER */}
//       <div className="relative w-[420px] max-w-[95%] h-full flex flex-col bg-[#1e1e2d] shadow-2xl border-l border-white/20 text-white">

//         {/* HEADER */}
//         <div className="flex justify-between items-center p-4 border-b border-white/10 bg-gradient-to-r from-[#2d1c6a] to-[#24155a]">
//           <h3 className="text-lg font-semibold tracking-wide ml-2 flex items-center gap-2">
//             Team Chat
//           </h3>

//           <div className="flex items-center gap-3">
//             {isAdmin && (
//               <button
//                 onClick={() => setShowCreateGroup(true)}
//                 title="Create Group"
//                 className="w-8 h-8 rounded-md bg-white/20 hover:bg-white/30 flex items-center justify-center transition border border-white/10"
//               >
//                 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
//                   <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
//                   <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
//                 </svg>
//               </button>
//             )}
//             <button
//               onClick={onClose}
//               className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
//             >
//               ✕
//             </button>
//           </div>
//         </div>

//         {/* SEARCH BAR */}
//         {!selectedUser && !selectedGroup && (
//           <div className="p-4 border-b border-white/10 bg-black/20 relative z-50">
//             <div className="relative" ref={searchRef}>
//               <input 
//                 type="text" 
//                 placeholder="Search users to start a chat..." 
//                 value={searchQuery}
//                 onFocus={() => setIsSearching(searchQuery.trim().length > 0)}
//                 onChange={(e) => {
//                   setSearchQuery(e.target.value);
//                   setIsSearching(e.target.value.trim().length > 0);
//                 }}
//                 className="w-full bg-white/5 text-white text-sm rounded-lg pl-10 pr-10 py-2.5 outline-none border border-white/10 focus:border-purple-500 placeholder:text-gray-400 transition" 
//               />
//               <svg 
//                 className="absolute left-3 top-2.5 text-gray-400 w-5 h-5 pointer-events-none" 
//                 fill="none" 
//                 stroke="currentColor" 
//                 viewBox="0 0 24 24"
//               >
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
//               </svg>

//               {searchQuery && (
//                 <button
//                   onClick={() => {
//                     setSearchQuery("");
//                     setIsSearching(false);
//                     fetchSearchResults(""); 
//                   }}
//                   className="absolute right-3 top-2.5 text-gray-400 hover:text-white transition"
//                 >
//                   ✕
//                 </button>
//               )}

//               {/* DROPDOWN */}
//               {isSearching && (
//                 <div className="absolute top-full left-0 w-full mt-2 bg-[#1e1e2d] border border-white/10 rounded-xl shadow-2xl max-h-64 overflow-y-auto custom-scrollbar z-[10000]">
//                   {searchResults.length === 0 ? (
//                     <div className="p-4 text-center text-gray-400 text-sm">No users found</div>
//                   ) : (
//                     searchResults.map(u => (
//                       <div 
//                         key={u.id}
//                         onClick={() => {
//                           setSelectedUser(u);
//                           setIsSearching(false);
//                           setSearchQuery("");
//                         }}
//                         className="flex items-center gap-3 p-3 hover:bg-white/10 cursor-pointer border-b border-white/5 last:border-0"
//                       >
//                         <div className="w-8 h-8 rounded-full overflow-hidden bg-white/20">
//                           {u.profile_pic ? (
//                             <img src={`${IMAGE_BASE_URL}${u.profile_pic}`} alt="profile" className="w-full h-full object-cover"/>
//                           ) : (
//                             <div className="w-full h-full flex items-center justify-center bg-purple-500 text-white font-semibold text-xs">
//                               {u.first_name?.charAt(0)}
//                             </div>
//                           )}
//                         </div>
//                         <div>
//                           <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
//                           {/* <p className="text-[10px] text-gray-400">{u.email}</p> */}
//                         </div>
//                       </div>
//                     ))
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         )}

//         {/* BODY */}
//         <div className="flex-1 overflow-y-auto p-2 flex flex-col custom-scrollbar">

//           {selectedUser || selectedGroup ? (
//             <ChatWindow
//               user={selectedUser}
//               group={selectedGroup}
//               currentUser={currentUser}
//               onBack={() => {
//                 setSelectedUser(null);
//                 setSelectedGroup(null);
//                 // Refresh list on back to get updated times/unread
//                 fetchGroups();
//                 fetchChatUsers();
//               }}
//               onInspectGroup={() => setInspectGroup(selectedGroup)}
//               onGroupDeleted={() => {
//                 setSelectedGroup(null);
//                 fetchGroups();
//               }}
//             />
//           ) : (
//             <div className="flex flex-col gap-1 pb-10">
              
//               {combinedChats.map(item => {
//                 if (item.isGroup) {
//                   return (
//                     <div
//                       key={`g_${item.id}`}
//                       onClick={() => setSelectedGroup(item)}
//                       className="flex items-center gap-3 p-3 hover:bg-white/10 rounded-xl cursor-pointer transition mb-1 border border-transparent hover:border-white/5"
//                     >
//                       <div className="w-12 h-12 rounded-full bg-purple-600/50 flex items-center justify-center font-bold text-white shadow-lg overflow-hidden border border-purple-400/30">
//                         {item.name.charAt(0).toUpperCase()}
//                       </div>

//                       <div className="flex-1">
//                         <p className="font-medium text-[15px]">{item.name}</p>
//                         <p className="text-xs text-gray-400 mt-0.5">Group</p>
//                       </div>

//                       {item.unread_count > 0 && (
//                         <div className="bg-red-500 w-5 h-5 flex items-center justify-center text-[10px] font-bold rounded-full text-white shadow">
//                           {item.unread_count}
//                         </div>
//                       )}
//                     </div>
//                   );
//                 } else {
//                   return (
//                     <div
//                       key={`u_${item.id}`}
//                       onClick={() => setSelectedUser(item)}
//                       className="flex items-center gap-3 p-3 hover:bg-white/10 rounded-xl cursor-pointer transition mb-1 border border-transparent hover:border-white/5"
//                     >
//                       {/* <div className="w-12 h-12 rounded-full overflow-hidden bg-white/20 shadow-lg border border-white/10">
//                         {item.profile_pic ? (
//                           <img
//                             src={`${IMAGE_BASE_URL}${item.profile_pic}`}
//                             alt="profile"
//                             className="w-full h-full object-cover"
//                           />
//                         ) : (
//                           <div className="w-full h-full flex items-center justify-center bg-[#2b195c] text-white font-bold opacity-90">
//                             {item.first_name?.charAt(0)}
//                           </div>
//                         )}
//                       </div> */}

// <div className="relative w-12 h-12">
//   <div className="w-12 h-12 rounded-full overflow-hidden bg-white/20">
//     {item.profile_pic ? (
//       <img
//         src={`${IMAGE_BASE_URL}${item.profile_pic}`}
//         alt="profile"
//         className="w-full h-full object-cover"
//       />
//     ) : (
//       <div className="w-full h-full flex items-center justify-center bg-[#2b195c] text-white font-bold">
//         {item.first_name?.charAt(0)}
//       </div>
//     )}
//   </div>

//   {/* 🟢 Teams-style ONLINE DOT */}
//   {onlineUsers.includes(item.id) && (
//     <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm"></span>
//   )}
// </div>

//                       <div className="flex-1">
//                         <p className="font-medium text-[15px]">
//                           {item.first_name} {item.last_name}
//                         </p>
//                         {/* <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">{item.email}</p> */}
//                       </div>

//                       {item.unread_count > 0 && (
//                         <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-[10px] font-bold text-white shadow">
//                           {item.unread_count}
//                         </div>
//                       )}
//                     </div>
//                   );
//                 }
//               })}

//               {combinedChats.length === 0 && (
//                  <p className="text-center text-sm text-gray-400 mt-10">No chats found. Use the search bar above to start a conversation.</p>
//               )}

//             </div>
//           )}
//         </div>

//         {/* MODALS */}
//         {showCreateGroup && (
//           <CreateGroupModal
//             employees={allEmployees}
//             onClose={() => setShowCreateGroup(false)}
//             onGroupCreated={fetchGroups}
//           />
//         )}

//         {inspectGroup && (
//           <GroupMembersModal
//             group={inspectGroup}
//             onClose={() => setInspectGroup(null)}
//             employees={allEmployees}
//             isAdmin={isAdmin}
//           />
//         )}
//       </div>
//     </div>,
//     document.body
//   );
// }
